import { i18n } from '../i18n.js';
import { MoonfallAttribution } from '../attribution.js';
import { MoonfallConsent } from '../analytics/consent-state.js';
import { MoonfallClickGuard } from '../utils/click-guard.js';
import { LEGAL_VERSIONS } from '../legal-versions.js';
import { MoonfallCloud } from '../convex-client.js';
/**
 * GDPR Settings - Privacy & Data controls
 * Provides Download My Data and Delete My Account functionality.
 */


import { ConsentGate } from './consent-gate.js';
import { AvatarPicker } from './avatar-picker.js';
import { Firebase } from '../firebase-loader.js';
let GdprSettings = {};

GdprSettings = (function () {
  let deleteOverlay = null;

  function uiText(key, fallback, replacements = {}) {
    const translated = i18n?.t?.(key, replacements);
    return translated && translated !== key ? translated : fallback;
  }

  function el(tag, attrs, children) {
    const node = document.createElement(tag);
    if (attrs) {
      Object.entries(attrs).forEach(([k, v]) => {
        if (k === "className") node.className = v;
        else if (k === "textContent") node.textContent = v;
        else if (k === "disabled") node.disabled = v;
        else if (k === "placeholder") node.placeholder = v;
        else if (k === "type") node.type = v;
        else node.setAttribute(k, v);
      });
    }
    (children || []).forEach((child) => {
      if (typeof child === "string") node.appendChild(document.createTextNode(child));
      else if (child) node.appendChild(child);
    });
    return node;
  }

  /**
   * Create the GDPR settings section DOM.
   * Append this to your settings/profile panel.
   */
  function createSettingsSection() {
    const section = el("div", { className: "gdpr-settings-section" });

    section.appendChild(el("div", { className: "gdpr-settings-title", textContent: uiText("gdpr.title", "Privacy & Data") }));

    // Download My Data
    const exportRow = el("div", { className: "gdpr-action-row" });
    const exportInfo = el("div", { className: "gdpr-action-info" });
    exportInfo.appendChild(el("div", { className: "gdpr-action-label", textContent: uiText("gdpr.downloadLabel", "Download My Data") }));
    exportInfo.appendChild(el("div", { className: "gdpr-action-desc", textContent: uiText("gdpr.downloadDesc", "Request an account-data export.") }));
    exportRow.appendChild(exportInfo);
    const exportBtn = el("button", { className: "gdpr-action-btn", textContent: uiText("gdpr.exportButton", "Export") });
    exportBtn.addEventListener("click", handleExport);
    exportRow.appendChild(exportBtn);
    section.appendChild(exportRow);

    // Optional consents (withdrawable anytime), one toggle per purpose:
    // measurement (attribution + analytics) and email news.
    const marketingRow = el("div", { className: "gdpr-action-row" });
    const marketingInfo = el("div", { className: "gdpr-action-info" });
    marketingInfo.appendChild(el("div", { className: "gdpr-action-label", textContent: uiText("gdpr.marketingLabel", "Analytics") }));
    marketingInfo.appendChild(el("div", {
      className: "gdpr-action-desc",
      textContent: uiText("gdpr.marketingDesc", "Helps us measure how players find Moonfall, linked to your account, so we can improve the game. Turn it off anytime."),
    }));
    marketingRow.appendChild(marketingInfo);
    // Both toggles wait, disabled, for the stored decision to arrive: an
    // enabled toggle showing "off" before the read lands is a lie the player
    // can act on (and a flip during that window would post the wrong value).
    const marketingToggle = el("input", { type: "checkbox", className: "gdpr-action-toggle", id: "gdpr-analytics-toggle", disabled: true });
    marketingToggle.setAttribute("aria-label", uiText("gdpr.marketingLabel", "Analytics"));
    marketingToggle.addEventListener("change", () => handleMarketingToggle(marketingToggle));
    marketingRow.appendChild(marketingToggle);
    section.appendChild(marketingRow);

    const emailRow = el("div", { className: "gdpr-action-row" });
    const emailInfo = el("div", { className: "gdpr-action-info" });
    emailInfo.appendChild(el("div", { className: "gdpr-action-label", textContent: uiText("gdpr.emailLabel", "Email news") }));
    emailInfo.appendChild(el("div", {
      className: "gdpr-action-desc",
      textContent: uiText("gdpr.emailDesc", "News about Moonfall by email — new sets, events and offers. Turn it off anytime; every email also has an unsubscribe link."),
    }));
    emailRow.appendChild(emailInfo);
    const emailToggle = el("input", { type: "checkbox", className: "gdpr-action-toggle", id: "gdpr-email-toggle", disabled: true });
    emailToggle.setAttribute("aria-label", uiText("gdpr.emailLabel", "Email news"));
    emailToggle.addEventListener("change", () => handleEmailToggle(emailToggle));
    emailRow.appendChild(emailToggle);
    section.appendChild(emailRow);
    refreshOptionalConsentState(marketingToggle, emailToggle);

    // Delete My Account
    const deleteRow = el("div", { className: "gdpr-action-row" });
    const deleteInfo = el("div", { className: "gdpr-action-info" });
    deleteInfo.appendChild(el("div", { className: "gdpr-action-label", textContent: uiText("gdpr.deleteLabel", "Delete My Account") }));
    deleteInfo.appendChild(el("div", {
      className: "gdpr-action-desc",
      textContent: uiText("gdpr.deleteDesc", "Request deletion of your account and associated data, subject to applicable retention requirements."),
    }));
    deleteRow.appendChild(deleteInfo);
    const deleteBtn = el("button", { className: "gdpr-action-btn danger", textContent: uiText("gdpr.deleteButton", "Delete") });
    deleteBtn.addEventListener("click", showDeleteConfirmation);
    deleteRow.appendChild(deleteBtn);
    section.appendChild(deleteRow);

    // Legal links
    const links = el("div", { className: "gdpr-legal-links" });
    const tosLink = el("a", { href: "/terms", target: "_blank", rel: "noopener", textContent: uiText("gdpr.terms", "Terms of Service") });
    const privacyLink = el("a", { href: "/privacy", target: "_blank", rel: "noopener", textContent: uiText("gdpr.privacy", "Privacy Policy") });
    const dmcaLink = el("a", { href: "/dmca", target: "_blank", rel: "noopener", textContent: uiText("legal.dmca", "Copyright") });
    links.appendChild(tosLink);
    links.appendChild(privacyLink);
    links.appendChild(dmcaLink);
    section.appendChild(links);

    return section;
  }

  async function getAuthHeaders() {
    if (!Firebase) return null;
    const token = await Firebase.getIdToken();
    if (!token) return null;
    return {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    };
  }

  function marketingVersion() {
    return (LEGAL_VERSIONS && LEGAL_VERSIONS.marketing)
      || (ConsentGate && ConsentGate.CURRENT_MARKETING_VERSION)
      || "";
  }

  function emailVersion() {
    return (LEGAL_VERSIONS && LEGAL_VERSIONS.emailMarketing)
      || (ConsentGate && ConsentGate.CURRENT_EMAIL_MARKETING_VERSION)
      || "";
  }

  async function refreshOptionalConsentState(marketingToggle, emailToggle) {
    try {
      if (!MoonfallCloud || !MoonfallCloud.attribution) return;
      const mine = await MoonfallCloud.attribution.mine();
      marketingToggle.checked = Boolean(mine && mine.marketingConsent && mine.marketingConsent.granted);
      emailToggle.checked = Boolean(mine && mine.emailMarketingConsent && mine.emailMarketingConsent.granted);
    } catch (e) {
      // Leave unchecked on failure; the user can still toggle them on.
    } finally {
      marketingToggle.disabled = false;
      emailToggle.disabled = false;
    }
  }

  async function handleEmailToggle(toggle) {
    const headers = await getAuthHeaders();
    if (!headers) {
      toggle.checked = !toggle.checked;
      alert(uiText("gdpr.signInMarketing", "Please sign in to change this setting."));
      return;
    }
    const granted = toggle.checked;
    try {
      const res = await fetch("/api/gdpr/consent", {
        method: "POST",
        headers,
        body: JSON.stringify({ consentType: "email_marketing", version: emailVersion(), granted, source: "settings" }),
      });
      if (!res.ok) throw new Error("status " + res.status);
    } catch (err) {
      console.error("[GDPR] email toggle failed:", err);
      toggle.checked = !granted; // revert optimistic UI
      alert(uiText("gdpr.marketingFailed", "Could not update this setting. Please try again."));
    }
  }

  async function handleMarketingToggle(toggle) {
    const headers = await getAuthHeaders();
    if (!headers) {
      toggle.checked = !toggle.checked;
      alert(uiText("gdpr.signInMarketing", "Please sign in to change this setting."));
      return;
    }
    const granted = toggle.checked;
    try {
      const res = await fetch("/api/gdpr/consent", {
        method: "POST",
        headers,
        body: JSON.stringify({ consentType: "marketing", version: marketingVersion(), granted, source: "settings" }),
      });
      if (!res.ok) throw new Error("status " + res.status);
      if (MoonfallAttribution) {
        if (granted) await MoonfallAttribution.commit();
        else MoonfallAttribution.clearPending();
      }
      // Same opt-in governs PostHog: enabling/disabling here turns analytics on/off live.
      if (MoonfallConsent) {
        MoonfallConsent.setAnalyticsConsent(granted);
      }
    } catch (err) {
      console.error("[GDPR] marketing toggle failed:", err);
      toggle.checked = !granted; // revert optimistic UI
      alert(uiText("gdpr.marketingFailed", "Could not update this setting. Please try again."));
    }
  }

  async function handleExport() {
    const headers = await getAuthHeaders();
    if (!headers) {
      alert(uiText("gdpr.signInExport", "Please sign in to export your data."));
      return;
    }

    try {
      const res = await fetch("/api/gdpr/export", { headers });
      if (!res.ok) throw new Error(uiText("gdpr.exportFailedStatus", "Export failed: {{status}}", { status: res.status }));
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "moonfall-data-export.json";
      // Plumbing for the download, never a control the player pressed.
      a.dataset.pressSound = "off";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("[GDPR] Export failed:", err);
      alert(uiText("gdpr.exportFailed", "Failed to export data. Please try again."));
    }
  }

  function showDeleteConfirmation() {
    if (deleteOverlay) hideDeleteConfirmation();

    deleteOverlay = el("div", { className: "gdpr-delete-overlay" });
    const modal = el("div", { className: "gdpr-delete-modal" });

    modal.appendChild(el("h3", { textContent: uiText("gdpr.modalTitle", "Delete Your Account?") }));
    modal.appendChild(el("p", { textContent: uiText("gdpr.modalIntro", "This requests permanent deletion of the following account access and Service data, subject to the retention described in the Privacy Policy:") }));

    const list = el("ul");
    [
      uiText("gdpr.deleteItems.collectionDecks", "Your card collection & decks"),
      uiText("gdpr.deleteItems.stardust", "All Stardust balance"),
      uiText("gdpr.deleteItems.moonDreamFoundry", "Moon Dream characters & Foundry art"),
      uiText("gdpr.deleteItems.exchangeListings", "Moon Exchange listings"),
      uiText("gdpr.deleteItems.subscription", "Your subscription (if active)"),
    ].forEach((text) => list.appendChild(el("li", { textContent: text })));
    modal.appendChild(list);

    const warning = el("p");
    const strong = el("strong", { textContent: uiText("gdpr.irreversible", "This action CANNOT be undone.") });
    strong.style.color = "#e86060";
    warning.appendChild(strong);
    modal.appendChild(warning);

    modal.appendChild(el("p", { textContent: uiText("gdpr.confirmPrompt", "Type DELETE to confirm:") }));

    const input = el("input", {
      className: "gdpr-delete-input",
      type: "text",
      placeholder: "DELETE",
    });
    modal.appendChild(input);

    const actions = el("div", { className: "gdpr-delete-actions" });
    const cancelBtn = el("button", { className: "gdpr-delete-cancel", textContent: uiText("gdpr.cancel", "Cancel") });
    const confirmBtn = el("button", { className: "gdpr-delete-confirm", textContent: uiText("gdpr.deleteForever", "Delete Forever"), disabled: true });

    input.addEventListener("input", () => {
      confirmBtn.disabled = input.value !== "DELETE";
    });

    cancelBtn.addEventListener("click", hideDeleteConfirmation);
    confirmBtn.addEventListener("click", () => {
      const action = async () => {
        confirmBtn.textContent = uiText("gdpr.deleting", "Deleting...");
        await handleDelete();
      };
      const guard = MoonfallClickGuard;
      if (guard && typeof guard.guard === "function") {
        guard.guard(confirmBtn, action, { cooldownMs: 5000 }).catch((err) => {
          console.error("[GDPR] delete guard failed:", err);
        });
      } else {
        action().catch((err) => console.error("[GDPR] delete failed:", err));
      }
    });

    actions.appendChild(cancelBtn);
    actions.appendChild(confirmBtn);
    modal.appendChild(actions);

    deleteOverlay.appendChild(modal);
    document.body.appendChild(deleteOverlay);

    requestAnimationFrame(() => {
      deleteOverlay.classList.add("visible");
      input.focus();
    });
  }

  /*
   * THE PRIVACY & DATA PANEL, REACHABLE FROM THE ACCOUNT MENU.
   *
   * `createSettingsSection` — the export button, the optional-consent toggles
   * and the legal links — had no mount since it was written (2026-04-08): the
   * account menu wired only Download and Delete, so the analytics toggle that
   * the Privacy Policy said was "available in Privacy & Data settings" was not
   * reachable anywhere, and consent could be given but never withdrawn in the
   * product. With email news added, "unsubscribe anytime" would have been the
   * same false promise. This opens the section in the same scrollable overlay
   * the delete confirmation uses.
   */
  let privacyOverlay = null;

  function showPrivacySettings() {
    if (privacyOverlay) hidePrivacySettings();

    privacyOverlay = el("div", { className: "gdpr-delete-overlay gdpr-privacy-overlay" });
    const modal = el("div", { className: "gdpr-delete-modal gdpr-privacy-modal", role: "dialog", "aria-modal": "true" });
    modal.appendChild(createSettingsSection());
    const actions = el("div", { className: "gdpr-delete-actions" });
    const closeBtn = el("button", { className: "gdpr-delete-cancel", textContent: uiText("store.close", "Close") });
    closeBtn.addEventListener("click", hidePrivacySettings);
    actions.appendChild(closeBtn);
    modal.appendChild(actions);

    privacyOverlay.addEventListener("click", (event) => {
      if (event.target === privacyOverlay) hidePrivacySettings();
    });
    privacyOverlay.appendChild(modal);
    document.body.appendChild(privacyOverlay);

    requestAnimationFrame(() => {
      privacyOverlay.classList.add("visible");
      closeBtn.focus();
    });
  }

  function hidePrivacySettings() {
    if (!privacyOverlay) return;
    const overlay = privacyOverlay;
    privacyOverlay = null;
    overlay.classList.remove("visible");
    setTimeout(() => {
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    }, 300);
  }

  function hideDeleteConfirmation() {
    if (!deleteOverlay) return;
    deleteOverlay.classList.remove("visible");
    setTimeout(() => {
      if (deleteOverlay && deleteOverlay.parentNode) {
        deleteOverlay.parentNode.removeChild(deleteOverlay);
      }
      deleteOverlay = null;
    }, 300);
  }

  async function handleDelete() {
    const headers = await getAuthHeaders();
    if (!headers) {
      alert(uiText("gdpr.signInDelete", "Please sign in to delete your account."));
      hideDeleteConfirmation();
      return;
    }

    try {
      const res = await fetch("/api/gdpr/delete", {
        method: "POST",
        headers,
        body: JSON.stringify({ confirmation: "DELETE" }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || uiText("gdpr.deletionFailed", "Deletion failed"));
      }

      hideDeleteConfirmation();

      /* The account's data is gone, whatever happens to its identity next:
         so is the avatar face this browser remembered for it (its uid and
         portrait URL, kept to start the face at the next sign-in). Sign-out
         and a refused login forget it the same way (main.js). */
      AvatarPicker?.forget?.();

      /* THE IDENTITY GOES TOO, not just the data.
         This used to erase every Convex row and then merely SIGN OUT, so the
         Firebase authentication record — the email address and auth identifier
         the Privacy Policy lists as account data — survived an account
         deletion, and a returning player was recognised as the same identity.
         Erasure has to include it.

         Firebase refuses a delete without a recent sign-in. That refusal is
         REPORTED, never swallowed: the data is already gone, so silently
         redirecting would tell the player their account was fully erased when
         one record of it remains. */
      let identity = { ok: false, code: "unavailable" };
      if (Firebase) {
        identity = await Firebase.deleteCurrentUser();
        if (!identity.ok) await Firebase.signOut();
      }
      if (!identity.ok && identity.code !== "no-current-user") {
        alert(uiText(
          "gdpr.identityRemains",
          "Your data has been deleted. Your sign-in record could not be removed "
          + "automatically — sign in again and retry to remove it completely.",
        ));
      }
      window.location.href = "/?account_deleted=true";
    } catch (err) {
      console.error("[GDPR] Account deletion failed:", err);
      alert(uiText("gdpr.deleteFailedAlert", "Failed to delete account: {{message}}", { message: err.message }));
      hideDeleteConfirmation();
    }
  }

  return {
    createSettingsSection,
    exportData: handleExport,
    showPrivacySettings,
    hidePrivacySettings,
    showDeleteConfirmation,
    hideDeleteConfirmation,
  };
})();

export { GdprSettings };
